import { ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { AppButton } from 'components/AppButton';
import { getTrans } from 'modules/i18n';

interface Props {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  onConfirm: () => void;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: 'primary' | 'error';
  /** Holds the confirm action back while what it needs is missing or already on its way, as FullScreenDialog does. */
  isConfirmDisabled?: boolean;
  /** Holds the way out back too, for a dialog whose action must not be left and re-entered while it is in flight. */
  isCancelDisabled?: boolean;
}

export const AlertDialog = ({
  isOpen,
  title,
  subtitle,
  onClose,
  onConfirm,
  children,
  confirmLabel,
  cancelLabel,
  confirmColor = 'primary',
  isConfirmDisabled,
  isCancelDisabled
}: Props) => (
  <Dialog
    open={isOpen}
    onClose={isCancelDisabled ? undefined : onClose}
    maxWidth="xs"
    fullWidth
  >
    <DialogTitle color="text.secondary" align="center" variant="h5">
      {title}
    </DialogTitle>

    {subtitle && (
      <DialogTitle
        color="text.secondary"
        align="center"
        variant="subtitle2"
        sx={{ pt: 0, mt: -2 }}
      >
        {subtitle}
      </DialogTitle>
    )}
    {children}
    <DialogActions sx={{ justifyContent: 'center' }}>
      <AppButton
        variant="outlined"
        sx={{ mr: '2.5%' }}
        onClick={onClose}
        disabled={isCancelDisabled}
      >
        {cancelLabel ?? getTrans('general.cancel')}
      </AppButton>
      <AppButton
        color={confirmColor}
        sx={{ ml: '2.5%' }}
        onClick={onConfirm}
        disabled={isConfirmDisabled}
      >
        {confirmLabel ?? getTrans('general.confirm')}
      </AppButton>
    </DialogActions>
  </Dialog>
);
